import { memo } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import waxSeal from '../../assets/ornaments/wax-seal.webp';
import { SpeakingRing, MutedMic } from '../voice/VoiceIndicators.js';
import { LevelDiamond } from '../../ui/LevelDiamond.js';
import { Glyph } from '../../ui/Glyph.js';

/**
 * Medallion + MedallionPickGrid — the desktop Activity's player-circle language.
 *
 * A Medallion is the round player disc (Discord avatar, or the Grenze initial
 * on an ink gradient). MedallionPickGrid lays medallions out in AT MOST TWO
 * rows and makes them the pick surface for night/vote actions: click a circle
 * to select it, a gold check badge stamps into its top-right corner. This
 * replaces the phone screens' generic list selectors on the desktop surface.
 *
 * States: selected (gold ring + glow + badge), locked (choice sealed — nothing
 * clickable, non-selected dim), dead/disabled (grayscale, inert). Entrance is
 * the PlayerRail stagger recipe; reduced-motion mounts static.
 *
 * The lifetime LEVEL badge lives on the Medallion itself, not the grid, so a
 * bare <Medallion> (TopBar's self chip, the players modal) and a grid cell wear
 * it identically. Top-left is the presence dot and top-right is the kick-X /
 * selection stamp, so it takes the free corner: bottom-centre, overlapping the
 * disc's edge. `LevelDiamond` renders nothing for a null level, which is what
 * makes "no badge until your first game" need no guard at any call site.
 */

/**
 * Level-badge geometry, as fractions of the disc's diameter. Exported because
 * the Stage's PlayerBust pins the same badge under a bigger portrait — one
 * proportion, so the badge reads the same size relative to the face everywhere.
 *
 * How far the level diamond hangs below the disc's bottom edge — enough that its
 * centre sits roughly ON the edge, so the badge reads as pinned TO the medallion
 * rather than floating under it. Kept at just under half the diamond's own
 * height (`LEVEL_SCALE / 2`): move the two apart and the badge floats free,
 * move them together and it creeps up over the face. Change one of these two
 * numbers and you must change the other.
 */
export const LEVEL_OVERHANG = 0.26;
/** Diamond width, over half the disc — the numeral has to be legible at 56px discs. */
export const LEVEL_SCALE = 0.55;

export interface MedallionProps {
  name: string;
  avatarUrl?: string | null;
  size?: number;
  /** Grayscale + dim (dead players, disabled targets). */
  dead?: boolean;
  /** Gold ring + glow (current selection). */
  selected?: boolean;
  /**
   * Presence dot in the circle's top-left (green = connected, grey = dropped).
   * Omit (undefined) on surfaces where presence is noise (night/vote picks).
   */
  connected?: boolean;
  /**
   * Discord user id — drives the talking-glow ring (reads live voice state). Omit
   * off Activity surfaces; the ring simply never shows.
   */
  discordUserId?: string | null;
  /**
   * Lifetime level — the diamond pinned under the disc. Absent/null draws
   * nothing (a seat that has never finished a game has no badge).
   */
  level?: number | null;
  /** Halo on the level diamond. Use for the local player's own medallion. */
  levelGlow?: boolean;
  /**
   * An AI SEAT — the troupe mask in the disc's free (bottom-right) corner.
   * Disclosed on purpose: an undisclosed bot that plays badly reads as a human
   * griefing (AI_SEATS_SPEC.md §6). Pass `connected: undefined` alongside it —
   * a green "online" dot on something with no socket is a lie.
   */
  ai?: boolean;
}

export function Medallion({
  name,
  avatarUrl,
  size = 72,
  dead = false,
  selected = false,
  connected,
  discordUserId,
  level,
  levelGlow = false,
  ai = false,
}: MedallionProps) {
  const initial = (name.trim()[0] ?? '?').toUpperCase();
  const dot = Math.max(9, Math.round(size * 0.16));
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <SpeakingRing discordUserId={discordUserId} />
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          border: selected ? '2px solid var(--gold-5, #e6b866)' : '1px solid rgba(201,164,78,0.35)',
          boxShadow: selected ? 'var(--rim-gold, 0 0 24px rgba(230,184,102,0.5))' : 'none',
          background: 'radial-gradient(circle at 35% 30%, var(--ink-3, #241619), var(--ink-1, #0b0709))',
          display: 'grid',
          placeItems: 'center',
          overflow: 'hidden',
          filter: dead ? 'grayscale(1) brightness(0.55)' : 'none',
          transition:
            'border-color var(--duration-base, 280ms) var(--ease-standard), box-shadow var(--duration-base, 280ms) var(--ease-standard), filter 600ms var(--ease-standard)',
        }}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: Math.round(size * 0.4),
              color: 'var(--gold-5, #e6b866)',
            }}
          >
            {initial}
          </span>
        )}
      </div>
      {connected !== undefined && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: 4,
            left: 4,
            width: dot,
            height: dot,
            borderRadius: '50%',
            background: connected ? '#4ade80' : 'var(--text-tertiary)',
            border: '2px solid var(--ink-0, #060406)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.6)',
            zIndex: 1,
          }}
        />
      )}
      {/* Level diamond — bottom-centre, overlapping the disc's edge. Inert to
          the pointer: the whole cell is usually a button.

          Stacking order inside the medallion cell (none of these boxes creates
          a stacking context, so these z-indexes compare directly, and an
          unlisted one is `auto` = DOM order):
            5  level diamond      bottom-centre
            4  SpeakingRing       fills the disc — it USED to paint over the
                                  diamond, which is why 5 is not 3
            2  kick-X             top-right (hover)
            1  presence dot       top-left
            auto  selection badge / wax seal — top-right, drawn after the disc
          Nothing is buried: the diamond only ever overlaps the ring, and the
          three corner marks sit in corners the diamond does not reach. */}
      {/* Troupe mask — the AI-seat mark, in the one corner nothing else uses
          (top-left is presence, top-right is kick/selection, bottom-centre is
          the level diamond). Ceremony glyph, never an emoji. */}
      {ai && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: Math.max(16, Math.round(size * 0.3)),
            height: Math.max(16, Math.round(size * 0.3)),
            borderRadius: '50%',
            background: 'var(--ink-0, #060406)',
            border: '1px solid rgba(139,92,246,0.5)',
            display: 'grid',
            placeItems: 'center',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        >
          <Glyph name="mask" tone="arcana" size={Math.max(10, Math.round(size * 0.18))} />
        </span>
      )}
      <span
        style={{
          position: 'absolute',
          bottom: -Math.round(size * LEVEL_OVERHANG),
          left: '50%',
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
          zIndex: 5,
        }}
      >
        <LevelDiamond level={level} size={Math.round(size * LEVEL_SCALE)} glow={levelGlow} />
      </span>
    </div>
  );
}

// ─── Pick grid ────────────────────────────────────────────────────────────────

export interface PickTarget {
  id: string;
  name: string;
  avatarUrl?: string | null;
  /** Discord user id — drives the talking-glow ring + muted-mic on this target. */
  discordUserId?: string | null;
  /** Rendered grayscale + inert (dead player shown for context, not pickable). */
  dead?: boolean;
  /** Presence dot (lobby/roster surfaces only — leave undefined for picks). */
  connected?: boolean;
  /** Marks the current player's own medallion ("you" tag under the name). */
  you?: boolean;
  /** Shows the grid's `onRemove` kick "X" on this target (host lobby only). */
  removable?: boolean;
  /** Lifetime level — the diamond under the disc. Null/absent = no badge. */
  level?: number | null;
  /** An AI seat — troupe mask badge + an "AI" tag beside the name. */
  ai?: boolean;
}

export interface MedallionPickGridProps {
  targets: PickTarget[];
  /** Currently selected target id (badge + gold ring). Single-pick callers. */
  selectedId?: string | null;
  /**
   * Multi-pick callers (One Night's Joker swaps TWO players): every id here
   * gets the badge + gold ring. `selectedId` alone could only ever light ONE
   * medallion, so a two-pick swap showed a single check and gave no clue who
   * the other half was. Takes precedence over `selectedId` when non-empty.
   */
  selectedIds?: readonly string[];
  /** Omit for a read-only roster grid (lobby, players modal previews). */
  onSelect?: (id: string) => void;
  /** Choice sealed — selection stays lit, everything else dims, clicks off. */
  locked?: boolean;
  size?: number;
  /**
   * Selection badge: 'check' (default), 'seal' — the wax-seal ballot stamp
   * from the phone VoteScreen, for votes ("seal your vote", not a checkbox) —
   * or 'kill' — a blood-red cross for the wolves' victim pick.
   */
  badge?: 'check' | 'seal' | 'kill';
  /**
   * Host-only kick (lobby roster): renders a hover-revealed "X" in the
   * top-right corner of every target with `removable: true`. Only meaningful
   * on read-only grids — never combine with `onSelect`.
   */
  onRemove?: (id: string) => void;
  /**
   * OPT-IN "click a face to read that player's profile", for ROSTER grids only
   * (the lobby). It is opt-in — and ignored whenever `onSelect` is present —
   * because this same grid is the night/vote PICK surface: on a picking screen
   * a click means "kill this player" or "vote this player", and a profile that
   * hijacked that click would cost someone the game. Never pass it together
   * with `onSelect`.
   *
   * Only fires for targets that carry a `discordUserId`; a web/room-code seat
   * has no profile, so it is neither clickable nor styled as clickable.
   */
  onOpenProfile?: (discordUserId: string) => void;
}

function MedallionPickGridImpl({ targets, selectedId = null, selectedIds, onSelect, locked = false, size = 76, badge = 'check', onRemove, onOpenProfile }: MedallionPickGridProps) {
  const reduced = useReducedMotion();
  // At most two rows: one row while it fits comfortably, then split in half.
  const cols = targets.length <= 5 ? targets.length : Math.ceil(targets.length / 2);
  // The diamond hangs below the disc, so the name row has to move down by the
  // overhang. Applied to EVERY cell the moment ANY seat wears one, or the names
  // in a mixed grid (some players levelled, some not) would sit at two heights.
  const levelPad = targets.some((t) => t.level != null) ? Math.round(size * LEVEL_OVERHANG) : 0;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.max(cols, 1)}, auto)`,
        justifyContent: 'center',
        gap: 'clamp(16px, 2.4vw, 30px)',
        width: '100%',
      }}
      role={onSelect ? 'listbox' : undefined}
      aria-label={onSelect ? 'Choose a player' : undefined}
    >
      {onRemove && (
        <style>{`
          /* ALL kick-X styling lives here (no inline styles) so the :hover /
             :active rules actually win the cascade. */
          .mpg-kick {
            position: absolute;
            top: -7px;
            right: -7px;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: rgba(6, 4, 6, 0.92);
            border: 1px solid rgba(201, 164, 78, 0.45);
            display: grid;
            place-items: center;
            color: var(--text-secondary);
            cursor: pointer;
            z-index: 2;
            opacity: 0;
            transition:
              opacity 160ms var(--ease-standard),
              color 160ms var(--ease-standard),
              border-color 160ms var(--ease-standard),
              background 160ms var(--ease-standard),
              transform 160ms var(--ease-standard),
              filter 160ms var(--ease-standard);
          }
          .mpg-item:hover .mpg-kick, .mpg-kick:focus-visible { opacity: 1; }
          /* Same feedback language as .rm-hover (cursor.css): pop on hover,
             press on click — plus the blood tint that says "remove". */
          .mpg-kick:hover {
            color: var(--blood-6, #ff5a4f);
            border-color: rgba(153, 0, 0, 0.7);
            background: rgba(38, 8, 10, 0.95);
            transform: scale(1.15);
          }
          .mpg-kick:active {
            transform: scale(0.92);
            filter: brightness(0.85);
          }
          @media (hover: none) { .mpg-item .mpg-kick { opacity: 1; } }
        `}</style>
      )}
      <AnimatePresence mode="popLayout" initial={false}>
        {targets.map((t, i) => {
          const selected =
            selectedIds && selectedIds.length > 0 ? selectedIds.includes(t.id) : t.id === selectedId;
          const clickable = !!onSelect && !locked && !t.dead;
          const dimmed = (locked && !selected) || t.dead;
          // Picking always wins: a profile can only ever be opened on a grid
          // that is NOT a pick surface (see `onOpenProfile`'s doc comment).
          const profileId = !onSelect && onOpenProfile && t.discordUserId ? t.discordUserId : null;
          // Host kick "X": needs the parent button NOT disabled so the nested
          // control receives pointer events (the parent itself stays a no-op).
          const removable = !!onRemove && !!t.removable;
          return (
            <motion.button
              key={t.id}
              type="button"
              layout
              layoutId={`mpg-${t.id}`}
              className="mpg-item"
              onClick={clickable ? () => onSelect(t.id) : profileId ? () => onOpenProfile!(profileId) : undefined}
              disabled={!clickable && !removable && !profileId}
              role={onSelect ? 'option' : undefined}
              aria-selected={onSelect ? selected : undefined}
              aria-label={profileId ? (t.you ? 'Your profile' : `${t.name}'s profile`) : undefined}
              aria-haspopup={profileId ? 'dialog' : undefined}
              initial={reduced ? false : { opacity: 0, y: 24, scale: 0.9 }}
              animate={{ opacity: dimmed && !selected ? 0.4 : 1, y: 0, scale: selected ? 1.05 : 1 }}
              // The spearhead cursor never changes shape over pick targets, so
              // hover itself must read: lift the medallion under the pointer.
              whileHover={(clickable || profileId) && !reduced ? { scale: selected ? 1.1 : 1.06 } : undefined}
              whileTap={(clickable || profileId) && !reduced ? { scale: 0.96 } : undefined}
              exit={reduced ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.9 }}
              transition={{
                duration: reduced ? 0 : 0.42,
                delay: reduced ? 0 : Math.min(i * 0.05, 0.4),
                ease: [0.22, 1, 0.36, 1],
              }}
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: clickable || profileId ? 'pointer' : 'default',
                font: 'inherit',
              }}
            >
              <div style={{ position: 'relative', marginBottom: levelPad }}>
                <Medallion name={t.name} avatarUrl={t.avatarUrl} size={size} dead={t.dead} selected={selected} connected={t.connected} discordUserId={t.discordUserId} level={t.level} ai={t.ai} />
                {/* Host kick — hover-revealed X in the circle's top-right (a
                    span, not a nested <button>: buttons can't nest). Touch
                    devices get it always-visible via the hover:none media. */}
                {removable && (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Remove ${t.name}`}
                    title={`Remove ${t.name}`}
                    className="mpg-kick"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove!(t.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemove!(t.id);
                      }
                    }}
                  >
                    <X size={14} strokeWidth={2.5} style={{ display: 'block' }} aria-hidden />
                  </span>
                )}
                {/* Selection badge — stamps into the circle's top-right. 'seal'
                    presses the wax-seal ballot stamp home (scale-down like a
                    real stamp); 'check' pops the gold check. */}
                <AnimatePresence>
                  {selected && badge === 'seal' && (
                    <motion.img
                      src={waxSeal}
                      alt=""
                      aria-hidden
                      initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 1.6, rotate: -12 }}
                      animate={{ opacity: 1, scale: 1, rotate: 0 }}
                      exit={{ opacity: 0, scale: reduced ? 1 : 0.6 }}
                      transition={{ duration: reduced ? 0 : 0.3, ease: [0.34, 1.56, 0.64, 1] }}
                      style={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        width: 34,
                        height: 34,
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.7))',
                      }}
                    />
                  )}
                  {selected && (badge === 'check' || badge === 'kill') && (
                    <motion.span
                      initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.4 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: reduced ? 1 : 0.4 }}
                      transition={{ duration: reduced ? 0 : 0.25, ease: [0.34, 1.56, 0.64, 1] }}
                      style={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background:
                          badge === 'kill'
                            ? 'linear-gradient(135deg, var(--blood-3, #802636) 0%, var(--blood-4, #990000) 100%)'
                            : 'var(--gold-4, #c9923f)',
                        border: '2px solid var(--ink-0, #060406)',
                        display: 'grid',
                        placeItems: 'center',
                        boxShadow: badge === 'kill' ? '0 0 12px rgba(153,0,0,0.65)' : '0 2px 8px rgba(0,0,0,0.6)',
                      }}
                      aria-hidden
                    >
                      {badge === 'kill' ? (
                        <X size={14} color="#ffffff" strokeWidth={3.5} />
                      ) : (
                        <Check size={14} color="var(--ink-0, #060406)" strokeWidth={3.5} />
                      )}
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  maxWidth: size + 24,
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 13,
                    color: selected ? 'var(--gold-5, #e6b866)' : t.dead ? 'var(--state-dead, #5c5c5c)' : 'var(--text-secondary)',
                    textDecoration: t.dead ? 'line-through' : 'none',
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    transition: 'color var(--duration-base, 280ms) var(--ease-standard)',
                  }}
                >
                  {t.name}
                  {t.you && (
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 11, marginLeft: 5 }}>you</span>
                  )}
                  {t.ai && (
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 11, marginLeft: 5 }}>AI</span>
                  )}
                </span>
                <MutedMic discordUserId={t.discordUserId} size={12} />
              </span>
            </motion.button>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

export const MedallionPickGrid = memo(MedallionPickGridImpl);
